import {
  Outlet,
  createRootRouteWithContext,
  HeadContent,
  Scripts,
  useRouter,
} from "@tanstack/react-router";
import { useEffect, useState, lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import appCssInline from "../styles.css?inline";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { NotFound } from "@/components/NotFound";
import { ReadingModeToggle, ReadingModeController } from "@/components/ReadingModeToggle";
import { AskScottWidget } from "@/components/AskScott/AskScottWidget";

function useIdleReady(delay = 1500) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    type IdleWin = Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    const w = window as IdleWin;
    if (typeof w.requestIdleCallback === "function") {
      const id = w.requestIdleCallback(() => setReady(true), { timeout: 2500 });
      return () => {
        const cancel = (window as unknown as { cancelIdleCallback?: (id: number) => void })
          .cancelIdleCallback;
        if (cancel) cancel(id);
      };
    }
    const t = window.setTimeout(() => setReady(true), delay);
    return () => window.clearTimeout(t);
  }, [delay]);
  return ready;
}

const LazyToaster = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })),
);
function DeferredToaster() {
  const ready = useIdleReady();
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <LazyToaster richColors position="bottom-right" />
    </Suspense>
  );
}

function DeferredAskScott() {
  const ready = useIdleReady();
  return ready ? <AskScottWidget /> : null;
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "author", content: "Scott Cunningham" },
      { name: "facebook-domain-verification", content: "5ue0dds78remn4y6fdyh909e5jeer7" },
      { name: "google-site-verification", content: "GqQ0XL0LgwHw-MSvs2PYwt4ocJwMj9wNDgSATORFei0" },
      { name: "google-site-verification", content: "hQQf4tJynxVCt4IFUbHR2fWaFWWxA8Fpzumgy-h4Pn8" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Scott Cunningham" },
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Scott Cunningham | Content Strategy & AI Workflows" },
      { property: "og:title", content: "Scott Cunningham | Content Strategy & AI Workflows" },
      { name: "twitter:title", content: "Scott Cunningham | Content Strategy & AI Workflows" },
      {
        name: "description",
        content:
          "Content strategy, technical copywriting, social media, and AI-assisted workflows for AI, cloud, Web3, cybersecurity, SaaS, and tech brands.",
      },
      {
        property: "og:description",
        content:
          "Content strategy, technical copywriting, social media, and AI-assisted workflows for AI, cloud, Web3, cybersecurity, SaaS, and tech brands.",
      },
      {
        name: "twitter:description",
        content:
          "Content strategy, technical copywriting, social media, and AI-assisted workflows for AI, cloud, Web3, cybersecurity, SaaS, and tech brands.",
      },
    ],
    links: [
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "shortcut icon", href: "/favicon.ico" },
      {
        rel: "preconnect",
        href: "https://fpwczoyanfzjqfaopcmr.supabase.co",
        crossOrigin: "anonymous",
      },
      { rel: "dns-prefetch", href: "https://fpwczoyanfzjqfaopcmr.supabase.co" },
      {
        rel: "alternate",
        type: "application/rss+xml",
        title: "Field Notes | Scott Cunningham",
        href: "https://scottcbusiness.com/rss.xml",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/fraunces-6NU58FyLNQOQZAnv9ZwNjucMHVn85Ni7emAe9lKqZTnbB-gzTK0K1ChjeveQ.woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/inter-UcC73FwrK3iLTeHuS_nVMrMxCp50SjIa1ZL7.woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/montserrat-700-latin.woff2",
        crossOrigin: "anonymous",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Person",
              "@id": "https://scottcbusiness.com/#person",
              name: "Scott Cunningham",
              alternateName: ["scottcbusiness", "@scottcbusiness", "Scott Cunningham Services"],
              jobTitle: "Social Media & Content Lead",
              url: "https://scottcbusiness.com",
              image: "https://scottcbusiness.com/og-default.jpg",
              description:
                "Social media and content lead helping AI, cloud, Web3, cybersecurity, SaaS, and consumer tech brands turn complex ideas into clear content systems and measurable growth.",
              email: "mailto:scottcbusiness@protonmail.com",
              sameAs: [
                "https://www.linkedin.com/in/scottcbusiness/",
                "https://x.com/scottcbusiness",
                "https://www.youtube.com/@scottcbusiness",
                "https://bsky.app/profile/scottcbusiness.com",
                "https://www.facebook.com/ScottCBusiness/",
              ],
              address: {
                "@type": "PostalAddress",
                addressLocality: "Toronto",
                addressCountry: "CA",
              },
              knowsAbout: [
                "Content strategy",
                "Social media",
                "Editorial planning",
                "Community growth",
                "AI-assisted content workflows",
              ],
            },
            {
              "@type": "ProfessionalService",
              "@id": "https://scottcbusiness.com/#service",
              name: "Scott Cunningham Content Strategy and AI Workflows",
              alternateName: ["scottcbusiness", "Scott Cunningham Services"],
              url: "https://scottcbusiness.com",
              image: "https://scottcbusiness.com/og-default.jpg",
              logo: "https://scottcbusiness.com/og-default.jpg",
              description:
                "Content strategy, technical copywriting, social media leadership, community growth, and AI-assisted workflow services for AI, cloud, Web3, cybersecurity, SaaS, and high-growth tech brands.",
              areaServed: "Worldwide",
              priceRange: "$$",
              founder: { "@id": "https://scottcbusiness.com/#person" },
              employee: { "@id": "https://scottcbusiness.com/#person" },
              hasOfferCatalog: {
                "@type": "OfferCatalog",
                name: "Services",
                itemListElement: [
                  "Content strategy",
                  "Technical copywriting",
                  "Social media management",
                  "Community growth",
                  "AI-assisted content workflows",
                ].map((s) => ({
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: s,
                    provider: { "@id": "https://scottcbusiness.com/#person" },
                  },
                })),
              },
              address: {
                "@type": "PostalAddress",
                addressLocality: "Toronto",
                addressCountry: "CA",
              },
            },
            {
              "@type": "WebSite",
              "@id": "https://scottcbusiness.com/#website",
              url: "https://scottcbusiness.com",
              name: "Scott Cunningham, Social Media & Content Lead",
              inLanguage: "en",
              description:
                "Portfolio of Scott Cunningham, a social media and content strategy lead helping brands build trust, audience, and revenue.",
              publisher: { "@id": "https://scottcbusiness.com/#person" },
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
});

function RootShell({ children }: { children: React.ReactNode }) {
  const { queryClient } = Route.useRouteContext();
  return (
    <html lang="en">
      <head>
        <HeadContent />
        <style dangerouslySetInnerHTML={{ __html: appCssInline }} />
        <script
          dangerouslySetInnerHTML={{
            __html:
              "try{var p=location.pathname;var isPost=p.indexOf('/blog/')===0&&p!=='/blog/tags';if(isPost&&localStorage.getItem('blog-reading-mode')==='light'){document.documentElement.classList.add('reading-light')}}catch(e){}",
          }}
        />
      </head>
      <body>
        <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const router = useRouter();
  useEffect(() => {
    let isPop = false;
    const onPop = () => {
      isPop = true;
      // Clear flag after the navigation resolves
      window.setTimeout(() => {
        isPop = false;
      }, 200);
    };
    window.addEventListener("popstate", onPop);
    const unsub = router.subscribe("onResolved", ({ toLocation, fromLocation }) => {
      if (fromLocation && toLocation.pathname === fromLocation.pathname) return;
      if (toLocation.hash) return;
      if (isPop) return; // preserve scroll on back/forward
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
    return () => {
      window.removeEventListener("popstate", onPop);
      unsub();
    };
  }, [router]);
  return (
    <div className="flex min-h-screen flex-col">
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <SiteHeader />
      <main id="main" className="relative flex-1 pt-[72px]">
        <Outlet />
      </main>
      <SiteFooter />
      <ReadingModeToggle variant="floating" />
      <ReadingModeController />
      <DeferredToaster />
      <DeferredAskScott />
    </div>
  );
}
